import { t } from '../i18n/translations';
import { Participant, Role } from '../types';

export class ParticipantDrawer {
  private participants: Participant[] = [];
  private waitingParticipants: Participant[] = [];
  private currentUserId = '';
  private currentUserRole: Role = 'participant';
  private containerEl: HTMLElement | null = null;
  private isVisible = false;

  public get isOpen(): boolean {
    return this.isVisible;
  }

  public onHostAction?: (action: string, targetId: string) => void;
  public onClose?: () => void;

  public setCurrentUser(id: string, role: Role) {
    this.currentUserId = id;
    this.currentUserRole = role;
  }

  public setParticipants(active: Participant[], waiting: Participant[] = []) {
    this.participants = active;
    this.waitingParticipants = waiting;
    if (this.isVisible && this.containerEl) {
      this.render(this.containerEl);
    }
  }

  public setSpeakingStatus(id: string, isSpeaking: boolean) {
    const p = this.participants.find(x => x.id === id);
    if (p) {
      p.is_speaking = isSpeaking;
      if (this.isVisible && this.containerEl) {
        this.render(this.containerEl);
      }
    }
  }

  public setRecordingStatus(id: string, isRecording: boolean) {
    const p = this.participants.find(x => x.id === id);
    if (p) {
      p.is_recording = isRecording;
      if (this.isVisible && this.containerEl) {
        this.render(this.containerEl);
      }
    }
  }

  public setScreenShareStatus(id: string, isSharing: boolean) {
    const p = this.participants.find(x => x.id === id);
    if (p) {
      p.screen_sharing = isSharing;
      if (this.isVisible && this.containerEl) {
        this.render(this.containerEl);
      }
    }
  }

  public setHandRaised(id: string, raised: boolean) {
    const p = this.participants.find(x => x.id === id);
    if (p) {
      p.hand_raised = raised;
      if (this.isVisible && this.containerEl) {
        this.render(this.containerEl);
      }
    }
  }

  public toggle(container: HTMLElement) {
    this.isVisible = !this.isVisible;
    if (this.isVisible) {
      this.render(container);
    } else {
      container.innerHTML = '';
    }
  }

  public hide() {
    this.isVisible = false;
    if (this.containerEl) {
      this.containerEl.innerHTML = '';
    }
  }

  public getIsOpen(): boolean {
    return this.isVisible;
  }

  public render(container: HTMLElement) {
    this.containerEl = container;
    const isHostOrMod = this.currentUserRole === 'host' || this.currentUserRole === 'moderator';

    container.innerHTML = `
      <div class="side-drawer">
        <div class="drawer-header">
          <h3>${t('participants')} (${this.participants.length})</h3>
          <button id="btn-close-participants" class="control-btn" style="width: 32px; height: 32px;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div class="drawer-body">
          <!-- Waiting Room Section (Only visible to Host/Mod) -->
          ${isHostOrMod && this.waitingParticipants.length > 0 ? `
            <div class="waiting-room-section" style="background: rgba(245, 158, 11, 0.08); border: 1.5px dashed var(--color-warning); border-radius: var(--radius-lg); padding: 12px; margin-bottom: 12px;">
              <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px;">
                <h4 style="font-size: 0.85rem; color: var(--color-warning); font-weight: 700; display: flex; align-items: center; gap: 6px; margin: 0;">
                  <span>⏳</span> Waiting Room (${this.waitingParticipants.length})
                </h4>
                <div style="display: flex; gap: 6px;">
                  <button id="btn-admit-all" class="btn-primary" style="padding: 4px 10px; font-size: 0.75rem; background: var(--color-success); border-color: var(--color-success); height: auto;">
                    Admit All
                  </button>
                  ${this.waitingParticipants.length > 1 ? `
                    <button id="btn-reject-all" class="btn-secondary" style="padding: 4px 8px; font-size: 0.75rem; height: auto;">
                      Reject All
                    </button>
                  ` : ''}
                </div>
              </div>
              <div style="display: flex; flex-direction: column; gap: 8px;">
                ${this.waitingParticipants.map(w => `
                  <div class="participant-item" style="padding: 8px 10px; background: var(--bg-surface-elevated); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); display: flex; align-items: center; justify-content: space-between;">
                    <div style="display: flex; align-items: center; gap: 8px; min-width: 0;">
                      <div style="width: 28px; height: 28px; border-radius: 50%; background: var(--brand-gradient); display: flex; align-items: center; justify-content: center; overflow: hidden; flex-shrink: 0; font-size: 0.75rem; font-weight: 700; color: white;">
                        ${w.avatar_url ? `<img src="${w.avatar_url}" style="width: 100%; height: 100%; object-fit: cover;" />` : (w.display_name[0]?.toUpperCase() || 'U')}
                      </div>
                      <span style="font-size: 0.85rem; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${w.display_name}</span>
                    </div>
                    <div style="display: flex; gap: 6px; flex-shrink: 0;">
                      <button class="btn-admit btn-primary" data-id="${w.id}" style="padding: 4px 10px; font-size: 0.75rem; background: var(--color-success); border-color: var(--color-success); font-weight: 600;">
                        ${t('admit')}
                      </button>
                      <button class="btn-reject btn-secondary" data-id="${w.id}" style="padding: 4px 8px; font-size: 0.75rem;">
                        ${t('reject')}
                      </button>
                    </div>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : ''}

          <!-- Host quick actions -->
          ${isHostOrMod && this.participants.length > 1 ? `
            <div style="display: flex; gap: 8px; margin-bottom: 10px;">
              <button id="btn-mute-all" class="btn-secondary" style="font-size: 0.8rem; padding: 6px 12px; width: 100%; justify-content: center; gap: 6px;">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                  <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                  <line x1="12" y1="19" x2="12" y2="23"></line>
                </svg>
                <span>Mute All Participants</span>
              </button>
            </div>
          ` : ''}

          <!-- In-call Participants Roster -->
          <div style="display: flex; flex-direction: column; gap: 8px;">
            ${this.participants.map(p => this.renderParticipantItem(p, isHostOrMod)).join('')}
          </div>
        </div>
      </div>
    `;

    this.bindEvents(container);
  }

  private renderParticipantItem(p: Participant, isHostOrMod: boolean): string {
    const isSelf = p.id === this.currentUserId;

    return `
      <div class="participant-item">
        <div class="participant-item-header">
          <div class="participant-info">
            <div style="width: 32px; height: 32px; border-radius: 50%; background: var(--brand-gradient); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.8rem; color: white; overflow: hidden; flex-shrink: 0;">
              ${p.avatar_url ? `<img src="${p.avatar_url}" style="width: 100%; height: 100%; object-fit: cover;" />` : (p.display_name[0]?.toUpperCase() || 'U')}
            </div>
            <div class="participant-name-container">
              <div class="participant-badges">
                <span class="participant-name" title="${p.display_name}">${p.display_name} ${isSelf ? '(You)' : ''}</span>
                ${p.role === 'host' ? `<span class="badge badge-host">Host</span>` : ''}
                ${(p.role === 'moderator' || p.is_cohost) ? `<span class="badge badge-moderator" style="background: rgba(168, 85, 247, 0.2); color: #c084fc; border: 1px solid rgba(168, 85, 247, 0.4); font-size: 0.68rem; padding: 1px 5px; border-radius: 4px; font-weight: 600;">Co-Host</span>` : ''}
                ${p.network_unstable ? `
                  <span class="badge-network-unstable" style="background: rgba(245, 158, 11, 0.2); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.4); font-size: 0.65rem; padding: 1px 5px; border-radius: 4px; font-weight: 600;" title="High latency / unstable internet connection">
                    📶 Unstable
                  </span>
                ` : ''}
                ${p.is_speaking ? `
                  <span class="badge-speaking-roster" title="Voice energy active">
                    <span class="voice-wave active">
                      <span class="wave-bar"></span>
                      <span class="wave-bar"></span>
                      <span class="wave-bar"></span>
                      <span class="wave-bar"></span>
                    </span>
                    Speaking
                  </span>
                ` : ''}
                ${p.is_recording ? `<span class="badge-recording-roster">● REC</span>` : ''}
                ${p.screen_sharing ? `<span class="badge-screenshare-roster">🖥️ Sharing</span>` : ''}
              </div>
              ${p.hand_raised ? `<span style="font-size: 0.72rem; color: #fbbf24; font-weight: 600; display: inline-flex; align-items: center; gap: 2px;">✋ Hand Raised</span>` : ''}
            </div>
          </div>

          <div class="participant-media-status">
            ${p.audio_muted ? `
              <span title="Microphone muted" style="display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: var(--color-danger-bg); color: var(--color-danger);">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                  <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                  <line x1="12" y1="19" x2="12" y2="23"></line>
                </svg>
              </span>
            ` : `
              <span title="Microphone unmuted (idle)" style="display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: rgba(148, 163, 184, 0.15); color: #94a3b8;">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                </svg>
              </span>
            `}

            ${p.video_muted ? `
              <span title="Camera off" style="display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: var(--bg-surface-elevated); color: var(--text-muted);">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                  <path d="M21 21l-3.34-3.34M23 7l-7 5 7 5V7z"></path>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                </svg>
              </span>
            ` : `
              <span title="Camera on" style="display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: rgba(59, 130, 246, 0.15); color: #60a5fa;">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <polygon points="23 7 16 12 23 17 23 7"></polygon>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                </svg>
              </span>
            `}
          </div>
        </div>

        <!-- Moderation Actions Toolbar (wrapped, cleanly separated below) -->
        ${isHostOrMod && !isSelf && p.role !== 'host' ? `
          <div class="participant-mod-actions">
            <button class="btn-mute-peer control-btn" data-id="${p.id}" title="${t('forceMute')}">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="1" y1="1" x2="23" y2="23"></line>
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12"></path>
              </svg>
              <span>Mute</span>
            </button>

            <button class="btn-stop-video-peer control-btn" data-id="${p.id}" title="Turn off camera">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="1" y1="1" x2="23" y2="23"></line>
                <path d="M21 21l-3.34-3.34M23 7l-7 5 7 5V7z"></path>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
              </svg>
              <span>Stop Cam</span>
            </button>

            ${p.screen_sharing ? `
              <button class="btn-stop-screen-peer control-btn active-danger" data-id="${p.id}" title="Stop screen share">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
                <span>Stop Screen</span>
              </button>
            ` : ''}

            ${p.hand_raised ? `
              <button class="btn-lower-hand-peer control-btn" data-id="${p.id}" style="background: rgba(245, 158, 11, 0.25); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.5);" title="Lower participant hand immediately">
                <span>✋</span>
                <span>Lower Hand</span>
              </button>
            ` : ''}

            ${!p.is_cohost && p.role !== 'moderator' ? `
              <button class="btn-make-cohost control-btn" data-id="${p.id}" title="Make Co-Host">
                <span>⭐</span>
                <span>Co-Host</span>
              </button>
            ` : ''}

            ${this.currentUserRole === 'host' ? `
              <button class="btn-transfer-host control-btn" data-id="${p.id}" title="Transfer Host Ownership">
                <span>👑</span>
                <span>Host</span>
              </button>
            ` : ''}

            <button class="btn-kick-peer control-btn active-danger" data-id="${p.id}" title="${t('kick')}">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              <span>Kick</span>
            </button>
          </div>
        ` : ''}
      </div>
    `;
  }

  private bindEvents(container: HTMLElement) {
    container.querySelector('#btn-close-participants')?.addEventListener('click', () => {
      this.hide();
      if (this.onClose) this.onClose();
    });

    container.querySelector('#btn-mute-all')?.addEventListener('click', () => {
      if (confirm('Mute microphone for all participants?')) {
        if (this.onHostAction) this.onHostAction('mute_all', '');
      }
    });

    container.querySelector('#btn-admit-all')?.addEventListener('click', () => {
      if (this.onHostAction) this.onHostAction('admit_all', '');
    });

    container.querySelector('#btn-reject-all')?.addEventListener('click', () => {
      if (confirm('Deny all waiting participants?')) {
        if (this.onHostAction) this.onHostAction('reject_all', '');
      }
    });

    container.querySelectorAll('.btn-admit').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).getAttribute('data-id');
        if (id && this.onHostAction) this.onHostAction('admit', id);
      });
    });

    container.querySelectorAll('.btn-reject').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).getAttribute('data-id');
        if (id && this.onHostAction) this.onHostAction('reject', id);
      });
    });

    container.querySelectorAll('.btn-mute-peer').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).getAttribute('data-id');
        if (id && this.onHostAction) this.onHostAction('mute', id);
      });
    });

    container.querySelectorAll('.btn-stop-video-peer').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).getAttribute('data-id');
        if (id && this.onHostAction) this.onHostAction('stop_video', id);
      });
    });

    container.querySelectorAll('.btn-stop-screen-peer').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).getAttribute('data-id');
        if (id && this.onHostAction) this.onHostAction('stop_screenshare', id);
      });
    });

    container.querySelectorAll('.btn-lower-hand-peer').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).getAttribute('data-id');
        if (id && this.onHostAction) {
          // Immediately lower hand without asking confirmation
          this.onHostAction('lower_hand', id);
        }
      });
    });

    container.querySelectorAll('.btn-make-cohost').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).getAttribute('data-id');
        if (id && confirm('Grant Co-Host moderation privileges to this participant?')) {
          if (this.onHostAction) this.onHostAction('make_cohost', id);
        }
      });
    });

    container.querySelectorAll('.btn-transfer-host').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).getAttribute('data-id');
        if (id && confirm('Transfer primary meeting Host ownership to this participant?')) {
          if (this.onHostAction) this.onHostAction('transfer_host', id);
        }
      });
    });

    container.querySelectorAll('.btn-kick-peer').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).getAttribute('data-id');
        if (id && confirm('Are you sure you want to remove this participant?')) {
          if (this.onHostAction) this.onHostAction('kick', id);
        }
      });
    });
  }
}
